// Package Imports
import { signOut } from "next-auth/react";

// Function Imports
import { isNotEmptyObject } from '@/lib/functions/isNotEmptyObject';

/**
 * Fetches a user's profile from the API using their ID and an authentication token.
 *
 * @param {number} id - The ID of the user whose profile is to be fetched.
 * @param {string} auth_token - The authentication token for API authorization.
 * @returns {Promise<{success: boolean, message: string, data: any}>} 
 * An object indicating whether the operation was successful, a message, and the user's profile data if successful.
 */
export async function getUserProfile(
  id: number,
  auth_token: string,
) {
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_SITE_URL}/api/customer/${id}/`, {
      method: 'GET',
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${auth_token}`,
        "Pragma": "no-cache",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        "Expires": "0",
      },
      cache: "no-store", // Prevent Next.js from caching
    });

    switch(response?.status) {
      case 200: {
        const data = await response.json();
        if (data && isNotEmptyObject(data)) {
          return { 'success': true, 'message': '', 'data': data };
        }
        return { 'success': false, 'message': 'Profile not found in response', 'data': null };
      }
      case 400: {
        const data = await response.json();
        return { 'success': false, 'message': data.error ?? '', 'data': null };
      }
      case 401: { 
        signOut({ callbackUrl: "/" });
        return { 'success': false, 'message': '', 'data': null };
      }
      default: {
        return {'success': false, 'message': 'Unknown error', 'data': null};
      }
    }
  }
  catch {
    return {'success': false, 'message': '', 'data': null};
  }
}
